import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { DataTransformer } from "@emed-labs/platform-utility-data-transformer";
import { findLastIndex, get, isEqual } from "lodash";
import { Config } from "../environment/config";
import {
	ConditionalNavigateConfig,
	NavigatorEvent,
	NavigatorEventType,
	NavigatorMenuItem,
	NavigatorPage,
	Theme
} from "../models/NavigatorModels";
import { BrowserUtils } from "../utils/BrowserUtils";
import { evaluateRecordMatchRule } from "../utils/RecordRuleUtils";

import { LoggerContext } from "@emed-labs/platform-utility-logger-context";
import { Procedure } from "../models/ProcedureModels";
import { EnvironmentContext } from "../providers/EnvironmentProvider";
import { ModalContext } from "../providers/ModalProvider";
import { ThemeContext } from "../providers/ThemeProvider";
import { VLabAPIContext } from "../providers/VLabAPIProvider";
import { PersistenceUtils } from "../utils/PersistenceUtils";
import { ProcedureScriptUtils } from "../utils/ProcedureScriptUtils";

import { useStateRef } from "../hooks/UseStateRef";
import { NavigatorDataUtils } from "../navigator/NavigatorDataUtils";
import { UserUIExtraNavigatorAttributes } from "../ui/pages/UserUI";
import { AuthContext } from "./AuthProvider";
import { AWSConnectAgentChildContext } from "./AWSConnectAgentChildProvider";
import { LanguageContext } from "./LanguageProvider";
import { SessionDataContext } from "./SessionDataProvider";
import { SessionStateContext } from "./SessionStateProvider";

const LOGGER = LoggerContext.create("navigator.Navigator");

// Module-scoped cache replacing the original static class field
const procedureSessionCache: Record<string, Procedure> = {};

export interface NavigatorProps {}

export interface NavigatorStateLike {
	currentPage: NavigatorPage | null;
	isLoading: boolean;
	pageStack: string[];
	procedureData?: Procedure;
}

/** Context value exposed by NavigatorProvider */
export interface NavigatorContext extends NavigatorStateLike {
	/** Derived helpers */
	getPageIDFromSyncID: (proc: Procedure | undefined, syncID: string) => string | undefined;
	getPage: (pageID: string | undefined, proc?: Procedure) => NavigatorPage | undefined;

	/** Actions */
	displayPage: (newPage: NavigatorPage | undefined, newPageStack?: string[]) => Promise<void>;
	navigateToSyncID: (syncID: string, newPageStack?: string[]) => Promise<void>;
	navigateToPageID: (pageID: string, newPageStack?: string[]) => Promise<void>;
	handleEvent: (event: NavigatorEvent) => void;
	handleOptionSelected: (option: NavigatorMenuItem) => Promise<void>;
	handleMoveBack: () => Promise<void>;

	/** Props needed by the view */
	persistenceID?: string;
	fireEvent: (type: NavigatorEventType, id?: string) => void;
	addScriptAttributes: (attributes: Record<string, any>) => void;
	scriptAttributes: Record<string, any>;
	setStartPageHints: (hints: { defaultPageID?: string; defaultSyncID?: string }) => void;

	/**
	 * Register a handler invoked when a script event has a `controlMessage` field
	 * (fires before navigate/menu updates run). Used by proctor UIs to wire the
	 * navigator to their existing Chime/Connect control message sender.
	 */
	registerControlMessageSender: (
		handler: ((id: string, data?: unknown) => void | Promise<void>) | null
	) => void;
}

export const NavigatorContext = createContext<NavigatorContext | null>(null);

export const useNavigator = () => {
	const ctx = useContext(NavigatorContext);
	if (!ctx) throw new Error("useNavigator must be used within a NavigatorProvider");
	return ctx;
};

/** Provider that encapsulates all Navigator logic/state. */
export function NavigatorProvider(props: PropsWithChildren) {
	const { children } = props;

	const sessionCtx = useContext(SessionDataContext);
	const Session = useContext(SessionStateContext);
	const vlabCtx = useContext(VLabAPIContext);
	const ModalCtx = useContext(ModalContext);
	const EnvCtx = useContext(EnvironmentContext);
	const LanguageCtx = useContext(LanguageContext);
	const AuthCtx = useContext(AuthContext);
	const AgentChildCtx = useContext(AWSConnectAgentChildContext);

	const { setTheme } = useContext(ThemeContext);

	const [currentPage, setCurrentPage, currentPageRef] = useStateRef<NavigatorStateLike["currentPage"]>(null);
	const [isLoading, setIsLoading] = useStateRef<boolean>(false);
	const [pageStack, setPageStack, pageStackRef] = useStateRef<string[]>([]);
	const [procedureData, setProcedureData, procedureDataRef] = useStateRef<Procedure | undefined>(undefined);

	const pendingEventsRef = useRef<{ type: NavigatorEventType; id?: string }[]>([]);
	const prevPageRef = useRef<NavigatorPage | null>(null);
	const controlMessageSenderRef = useRef<
		((id: string, data?: unknown) => void | Promise<void>) | null
	>(null);

	const [scriptAttributes, setScriptAttributes, scriptAttributesRef] = useStateRef<Record<string, any>>({
		CORE_BASE_URL: BrowserUtils.getOrigin(),
		WEB_BASE_URL: BrowserUtils.getOrigin()
	});
	const [startHints, setStartHints] = useState<{ defaultPageID?: string; defaultSyncID?: string } | null>(null);

	const persistenceID = useMemo(() => {
		if (AuthCtx.role === "patient") {
			// actor is the user
			return Session.sessionID ?? undefined;
		}
		if (AuthCtx.role === "proctor") {
			// actor is the proctor
			return AgentChildCtx.contactID ?? undefined;
		}
		// fallback if role not set
		return Session.sessionID ?? undefined;
	}, [AuthCtx.role, Session.sessionID, AgentChildCtx.contactID]);

	// expose to consumers (the View)
	const setStartPageHints = useCallback((hints: { defaultPageID?: string; defaultSyncID?: string }) => {
		setStartHints(hints ?? {}); // treat "initialized" even if empty object
	}, []);

	useEffect(() => {
		const attributes = {
			...scriptAttributesRef.current,
			...NavigatorDataUtils.createNavigatorAttributes<UserUIExtraNavigatorAttributes>({
				extraData: AgentChildCtx.defaultAttributes,
				sessionID: AgentChildCtx.contactAttributes?.sessionID ?? Session.sessionID,
				patient: AgentChildCtx.patientData ?? AuthCtx.patient,
				actor: AgentChildCtx.patientRoleActorData ?? AuthCtx.actor,
				procedureID: AgentChildCtx.contactAttributes?.procedureID ?? Session.procedureID,
				contactID: AgentChildCtx.contactID,
				env: EnvCtx,
				sessionTags: Session.sessionMetadata?.record?.data?.tag
			}),
			ENVIRONMENT: EnvCtx.get(Config.ENVIRONMENT)
		};
		if (!isEqual(attributes, scriptAttributesRef.current)) {
			setScriptAttributes(attributes);
		}
	}, [
		EnvCtx,
		Session.sessionID,
		Session.procedureID,
		Session.sessionMetadata,
		AuthCtx.patient,
		AuthCtx.actor,
		AgentChildCtx.defaultAttributes,
		AgentChildCtx.contactAttributes?.sessionID,
		AgentChildCtx.contactAttributes?.procedureID,
		AgentChildCtx.contactID,
		AgentChildCtx.patientData,
		AgentChildCtx.patientRoleActorData
	]);

	const addScriptAttributes = useCallback((attributes: Record<string, any>) => {
		setScriptAttributes((prev) => ({ ...prev, ...attributes }));
	}, []);

	const getProctorOrUserString = useCallback((location: `${string}/proctor` | `${string}/user`) => {
		const match = location.match(/^(.*?)(\/proctor|\/user)$/);
		return match ? match[1] : null;
	}, []);

	const setPersistedPageStack = useCallback(
		(newStack: string[]) => {
			if (!Session.sessionID) return;
			PersistenceUtils.setData(`${Session.sessionID}/${Session.procedureLocation}/pageStack`, newStack);
		},
		[Session.sessionID, Session.procedureLocation]
	);

	const getPersistedPageStack = useCallback(
		(startPage: NavigatorPage | null): string[] => {
			if (!Session.sessionID) return [];
			const persisted = PersistenceUtils.getData<string[]>(
				`${Session.sessionID}/${Session.procedureLocation}/pageStack`
			);
			let stack: string[] = [];
			if (Array.isArray(persisted)) {
				const lastStartIdx = findLastIndex(persisted, (id) => id === startPage?.id);
				if (lastStartIdx >= 0) {
					stack = persisted.slice(0, lastStartIdx + 1);
				}
			}
			return stack;
		},
		[Session.sessionID, Session.procedureLocation]
	);

	const getPageIDFromSyncID = useCallback((proc: Procedure | undefined, syncID: string): string | undefined => {
		if (!proc) return undefined;
		const page = proc.pages.find((p) => p.syncID && p.syncID === syncID);
		return page?.id;
	}, []);

	const getPage = useCallback(
		(pageID: string | undefined, proc?: Procedure): NavigatorPage | undefined => {
			if (!proc || !pageID) return undefined;
			const newPageIndex = proc.pages.findIndex((p) => p.id === pageID);
			if (newPageIndex === -1) return undefined;

			const ENVIRONMENT = EnvCtx.get(Config.ENVIRONMENT);
			const attrs = {
				...scriptAttributes,
				ENVIRONMENT,
				CORE_BASE_URL: BrowserUtils.getOrigin(),
				WEB_BASE_URL: BrowserUtils.getOrigin()
			};

			return DataTransformer.transform(proc, {
				dataPath: ["pages", newPageIndex.toString()],
				variables: attrs,
				language: LanguageCtx.language,
				environment: ENVIRONMENT
			}) as unknown as NavigatorPage;
		},
		[scriptAttributes, EnvCtx, LanguageCtx.language]
	);

	const getPersistentState = useCallback(
		(key: string) => {
			return PersistenceUtils.getData(`${Session.sessionID}/${Session.procedureID}/${key}`);
		},
		[Session.sessionID, Session.procedureID]
	);

	const loadProcedureData = useCallback(
		async (loc: string) => {
			let proc: Procedure | undefined = procedureSessionCache[loc];
			if (!proc) {
				const parts = loc.split("/");
				const role = parts.pop();
				const procedureName = parts.join("/");
				const procedurePath = await ProcedureScriptUtils.resolveScriptPath(procedureName);
				proc = await vlabCtx.loadFileFromVLabScriptsCDN(`/procedures/${procedurePath}/${role}.json`, true);
				procedureSessionCache[loc] = proc as Procedure;
			}

			setProcedureData(proc);
			// Don't call setupPage here - it will be called by the useEffect that watches procedureData
		},
		[vlabCtx]
	);

	const setupPage = useCallback(async () => {
		if (!procedureDataRef.current) return;

		// LEGACY precedence:
		// 1) defaultPageID
		// 2) defaultSyncID
		// 3) lastPage (optional resume behavior)
		// 4) first page
		const { defaultPageID, defaultSyncID } = startHints ?? {};
		let startPageID: string | undefined =
			defaultPageID ??
			(defaultSyncID ? getPageIDFromSyncID(procedureDataRef.current, defaultSyncID) : undefined) ??
			getPersistentState("lastPage") ??
			procedureDataRef.current.pages[0]?.id;

		const startPage = getPage(startPageID, procedureDataRef.current);
		const persistedStack = getPersistedPageStack(startPage ?? null);
		const newStack = persistedStack.length > 0 ? persistedStack : startPage ? [startPage.id] : [];

		if (!isEqual(currentPageRef.current, startPage)) {
			setCurrentPage(startPage ?? null);
		}
		if (!isEqual(pageStackRef.current, newStack)) {
			setPageStack(newStack);
		}
	}, [
		startHints, // NEW
		getPage,
		getPageIDFromSyncID,
		getPersistentState,
		getPersistedPageStack
	]);

	useEffect(() => {
		// do nothing until we have loaded procedure data AND the view has initialized hints at least once
		if (!procedureData || startHints === null) return;
		setupPage();
	}, [procedureData, startHints, setupPage]);

	// initial load + on location change
	useEffect(() => {
		(async () => {
			if (!Session.procedureLocation) return;
			await loadProcedureData(Session.procedureLocation);
		})();
	}, [Session.procedureLocation, loadProcedureData]);

	// on currentPage change: scroll, notify, process pending, initial checkpoint
	useEffect(() => {
		if (!isEqual(prevPageRef.current, currentPage)) {
			// scroll to top
			if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
			if (typeof document !== "undefined")
				document.querySelector(".site-content")?.scrollTo({ top: 0, behavior: "smooth" });

			/* 	if (onPageChanged) {
				onPageChanged(currentPage as NavigatorPage, prevPageRef.current as NavigatorPage);
			} */

			if (currentPage) {
				const events = [...pendingEventsRef.current];
				pendingEventsRef.current = [];
				events.forEach((e) => {
					// Use a ref to avoid dependency issues
					fireEvent(e.type, e.id);
				});
			}

			if (!prevPageRef.current && currentPage?.checkpointID) {
				(async () => {
					try {
						const procedureActor = getProctorOrUserString(Session.procedureLocation);
						if (procedureActor) {
							if (Array.isArray(currentPage.checkpointID)) {
								await Promise.all(
									currentPage.checkpointID.map((checkpoint) => {
										return sessionCtx.saveCheckpointEvent(procedureActor, checkpoint);
									})
								);
							} else {
								await sessionCtx.saveCheckpointEvent(procedureActor, currentPage.checkpointID);
							}
						}
					} catch (e) {
						LOGGER.error("initialCheckpoint", "error saving checkpoint", e, {
							sessionID: Session.sessionID
						});
					}
				})();
			}

			prevPageRef.current = currentPage ?? null;
		}
	}, [currentPage, Session.procedureLocation, Session.sessionID, getProctorOrUserString, sessionCtx]);

	// keep theme in sync
	useEffect(() => {
		const t = procedureData?.theme ?? get(currentPage, "theme", Theme.Light);
		setTheme(t);
	}, [currentPage, procedureData, setTheme]);

	const displayPage = useCallback(
		async (newPage: NavigatorPage | undefined, newPageStack?: string[]) => {
			try {
				if (!newPage) return;
				if (isEqual(newPage, currentPage)) return;

				if (newPage.checkpointID) {
					const procedureActor = getProctorOrUserString(Session.procedureLocation);
					if (procedureActor) {
						if (Array.isArray(newPage.checkpointID)) {
							await Promise.all(
								newPage.checkpointID.map((checkpoint) => {
									return sessionCtx.saveCheckpointEvent(procedureActor, checkpoint);
								})
							);
						} else {
							await sessionCtx.saveCheckpointEvent(procedureActor, newPage.checkpointID);
						}
					}
				}

				const stack = newPageStack ?? [...pageStack, newPage.id];
				setPersistedPageStack(stack);
				setCurrentPage(newPage);
				setPageStack(stack);
			} catch (e) {
				LOGGER.error("displayPage", "error occurred while saving checkpoint", e, {
					sessionID: Session.sessionID
				});
				await ModalCtx.alert("Something went wrong. Please try again.");
			}
		},
		[
			currentPage,
			getProctorOrUserString,
			pageStack,
			Session.procedureLocation,
			Session.sessionID,
			setPersistedPageStack,
			sessionCtx,
			ModalCtx
		]
	);

	const navigateToSyncID = useCallback(
		async (syncID: string, newPageStack?: string[]) => {
			const newPage = getPage(getPageIDFromSyncID(procedureData, syncID), procedureData);
			await displayPage(newPage, newPageStack);
		},
		[displayPage, getPage, getPageIDFromSyncID, procedureData]
	);

	const navigateToPageID = useCallback(
		async (pageID: string, newPageStack?: string[]) => {
			const newPage = getPage(pageID, procedureData);
			await displayPage(newPage, newPageStack);
		},
		[displayPage, getPage, procedureData]
	);

	const registerControlMessageSender = useCallback(
		(handler: ((id: string, data?: unknown) => void | Promise<void>) | null) => {
			controlMessageSenderRef.current = handler;
		},
		[]
	);

	const handleEvent = useCallback(
		(event: NavigatorEvent) => {
			if (event.controlMessage) {
				void controlMessageSenderRef.current?.(event.controlMessage.id, event.controlMessage.data);
			}
			if (event.navigate) {
				navigateToPageID(event.navigate);
			} else if (event.menu || event.content) {
				const updated: NavigatorPage = {
					...(currentPageRef.current as NavigatorPage),
					menu: event.menu || currentPageRef.current?.menu,
					content: event.content || currentPageRef.current?.content
				};
				setCurrentPage(updated);
			}
		},
		[navigateToPageID]
	);

	const fireEvent = (type: NavigatorEventType, id?: string) => {
		if (!currentPageRef.current) {
			pendingEventsRef.current.push({ type, id });
			return;
		}

		const events = currentPageRef.current.events?.filter((event) => {
			if (event.type === type) {
				if (id) return event.id === id;
				if (!event.id) return true;
			}
			return false;
		});

		events?.forEach((handler) => handleEvent(handler));
	};

	const handleOptionSelected = async (option: NavigatorMenuItem) => {
		if (!procedureData) return;
		switch (option.type) {
			case "navigate": {
				const navConfig = option.config as string | ConditionalNavigateConfig | undefined;
				let newPageID: string | undefined;

				if (navConfig && typeof navConfig === "object") {
					try {
						const matched = await evaluateRecordMatchRule(
							{ record: navConfig.record, match: navConfig.match },
							(type) => sessionCtx.getDataByType(type)
						);
						newPageID = matched ? navConfig.onMatch : navConfig.onMismatch;
						LOGGER.info("handleOptionSelected", "conditional navigate evaluated", { matched, newPageID });
					} catch (e) {
						LOGGER.error("handleOptionSelected", "failed to evaluate conditional navigate, using onMismatch", e);
						newPageID = navConfig.onMismatch;
					}
				} else {
					newPageID = navConfig as string | undefined;
				}

				if (!newPageID || newPageID === "next") {
					const idx = procedureData.pages.findIndex((p) => p.id === currentPage?.id);
					newPageID = get(procedureData.pages, [idx + 1, "id"]);
				}

				if (newPageID) await navigateToPageID(newPageID);
				break;
			}
			case "link":
			case "link.incognito":
			case "button": {
				fireEvent("click", option.id);
				break;
			}
		}
	};

	const handleMoveBack = useCallback(async () => {
		setIsLoading(true);
		try {
			if (pageStack.length < 1) return;

			let newStack = [...pageStack];
			// Use currentPageRef to avoid stale closure issues
			const backPageId =
				(currentPageRef.current?.allowBack as NavigatorEvent)?.navigate || newStack[newStack.length - 2];
			const backIdx = findLastIndex(newStack, (id) => id === backPageId);
			if (backIdx < 0) return;

			const backPage = getPage(backPageId, procedureDataRef.current);
			if (backPage) {
				newStack = newStack.slice(0, backIdx + 1);
				// Navigate directly
				await displayPage(backPage, newStack);
			}
		} finally {
			setIsLoading(false);
		}
	}, [displayPage, getPage, Session.sessionID, Session.procedureID, pageStack]);

	const value: NavigatorContext = {
		currentPage,
		isLoading,
		pageStack,
		procedureData,
		getPageIDFromSyncID,
		getPage,
		persistenceID,
		displayPage,
		navigateToSyncID,
		navigateToPageID,
		handleEvent,
		handleOptionSelected,
		handleMoveBack,
		fireEvent,
		addScriptAttributes,
		scriptAttributes,
		setStartPageHints,
		registerControlMessageSender
	};

	return <NavigatorContext.Provider value={value}>{children}</NavigatorContext.Provider>;
}
